import React, { useEffect, useMemo, useState } from "react";
import {
  Activity,
  Bell,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Eraser,
  AlertTriangle,
  RefreshCw,
  MoveHorizontal,
  Search,
  ShieldCheck,
} from "lucide-react";
import AdminSidebar from "../../components/admin/AdminSidebar";
import SmartDataTable from "../../components/shared/SmartDataTable";
import { adminApi } from "../../services/api";

const LIMIT = 25;

const ENTITY_OPTIONS = [
  "user",
  "pool",
  "ticket",
  "ticket_request",
  "result",
  "winner",
  "payment",
  "settings",
];

const ACTION_OPTIONS = [
  "create_pool",
  "update_pool",
  "cancel_pool",
  "announce_pool_outcome",
  "auto_draw",
  "upload_ticket",
  "update_ticket",
  "update_ticket_request",
  "enter_result",
  "run_checker",
  "update_user",
  "suspend_user",
  "mark_paid",
  "update_settings",
];

function readable(value) {
  if (!value) return "-";
  return String(value)
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (character) => character.toUpperCase());
}

function formatTimestamp(value) {
  if (!value) return { date: "-", time: "" };
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return { date: "-", time: "" };

  return {
    date: date.toLocaleDateString("en-US", {
      month: "short",
      day: "2-digit",
      year: "numeric",
    }),
    time: date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    }),
  };
}

function abbreviatedId(value) {
  if (!value) return "-";
  const id = String(value);
  return id.length > 16 ? `${id.slice(0, 7)}...${id.slice(-6)}` : id;
}

export default function AuditLogs() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [entity, setEntity] = useState("");
  const [action, setAction] = useState("");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [sort, setSort] = useState("createdAt");
  const [order, setOrder] = useState("desc");
  const [requestVersion, setRequestVersion] = useState(0);

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(1);
    }, 350);
    return () => window.clearTimeout(timeout);
  }, [search]);

  useEffect(() => {
    let active = true;

    async function loadLogs() {
      setLoading(true);
      setError("");
      try {
        const response = await adminApi.getLogs({
          page,
          limit: LIMIT,
          entity: entity || undefined,
          action: action || undefined,
          search: debouncedSearch || undefined,
          from: from || undefined,
          to: to || undefined,
          sort,
          order,
        });
        if (!active) return;
        const data = Array.isArray(response.data?.data) ? response.data.data : [];
        setLogs(data);
        setTotal(Number(response.data?.total) || 0);
      } catch (requestError) {
        if (!active) return;
        setLogs([]);
        setTotal(0);
        setError(requestError.response?.data?.message || "Failed to load audit logs.");
      } finally {
        if (active) setLoading(false);
      }
    }

    loadLogs();
    return () => {
      active = false;
    };
  }, [page, entity, action, debouncedSearch, from, to, sort, order, requestVersion]);

  const totalPages = Math.max(1, Math.ceil(total / LIMIT));
  const hasFilters = Boolean(entity || action || search || from || to);
  const visibleStart = total && logs.length ? (page - 1) * LIMIT + 1 : 0;
  const visibleEnd = total && logs.length ? visibleStart + logs.length - 1 : 0;

  useEffect(() => {
    if (!loading && page > totalPages) setPage(totalPages);
  }, [loading, page, totalPages]);

  const columns = useMemo(() => [
    {
      id: "createdAt",
      sortKey: "createdAt",
      name: "Timestamp",
      selector: (row) => row.createdAt || "",
      sortable: true,
      minWidth: "168px",
      cell: (row) => {
        const timestamp = formatTimestamp(row.createdAt);
        return (
          <span className="audit-timestamp">
            <strong>{timestamp.date}</strong>
            <small>{timestamp.time}</small>
          </span>
        );
      },
    },
    {
      id: "admin",
      name: "Administrator",
      selector: (row) => row.admin?.name || row.admin?.email || "",
      sortable: false,
      minWidth: "190px",
      grow: 1.2,
      cell: (row) => (
        <span className="audit-actor">
          <strong>{row.admin?.name || "Unknown admin"}</strong>
          <small>{row.admin?.email || "Account unavailable"}</small>
        </span>
      ),
    },
    {
      id: "action",
      sortKey: "action",
      name: "Action",
      selector: (row) => row.action || "",
      sortable: true,
      minWidth: "178px",
      cell: (row) => <span className="audit-action">{readable(row.action)}</span>,
    },
    {
      id: "entity",
      sortKey: "entity",
      name: "Entity",
      selector: (row) => row.entity || "",
      sortable: true,
      minWidth: "126px",
      cell: (row) => <span className="audit-entity">{readable(row.entity)}</span>,
    },
    {
      id: "entityId",
      sortKey: "entityId",
      name: "Reference ID",
      selector: (row) => row.entityId || "",
      sortable: true,
      minWidth: "168px",
      cell: (row) => (
        <code className="audit-reference" title={row.entityId || "No reference ID"}>
          {abbreviatedId(row.entityId)}
        </code>
      ),
    },
    {
      id: "ip",
      sortKey: "ip",
      name: "Source IP",
      selector: (row) => row.ip || "",
      sortable: true,
      minWidth: "138px",
      cell: (row) => <code className="audit-ip">{row.ip || "-"}</code>,
    },
  ], []);

  function updateFilter(setter, value) {
    setter(value);
    setPage(1);
  }

  function clearFilters() {
    setEntity("");
    setAction("");
    setSearch("");
    setDebouncedSearch("");
    setFrom("");
    setTo("");
    setPage(1);
  }

  function handleSort(column, direction) {
    setSort(column.sortKey || column.id);
    setOrder(direction);
    setPage(1);
  }

  return (
    <div className="admin-shell audit-admin-shell">
      <AdminSidebar />
      <main className="admin-main">
        <header className="admin-topbar audit-topbar">
          <div className="admin-title">Audit Logs</div>
          <div className="audit-topbar-context"><ShieldCheck size={15} /> Administrative record</div>
          <div className="system-live"><i /> System Live</div>
          <button className="icon-button" type="button" aria-label="Notifications"><Bell size={18} /><i /></button>
        </header>

        <div className="admin-content audit-content">
          <section className="audit-page-heading" aria-labelledby="audit-page-title">
            <div>
              <span className="audit-eyebrow">System oversight</span>
              <h1 id="audit-page-title">Audit Logs</h1>
              <p>Review administrator activity across protected platform operations.</p>
            </div>
            <div className="audit-summary" aria-label="Audit log summary">
              <div><Activity size={18} /><span>Total recorded<strong>{total.toLocaleString()}</strong></span></div>
              <div><ShieldCheck size={18} /><span>Visible now<strong>{logs.length.toLocaleString()}</strong></span></div>
            </div>
          </section>

          <section className="audit-filters" aria-label="Audit log filters">
            <label className="audit-search-field">
              <span>Search</span>
              <div><Search size={15} /><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Actor, action, entity..." /></div>
            </label>
            <label>
              <span>Entity</span>
              <select value={entity} onChange={(event) => updateFilter(setEntity, event.target.value)}>
                <option value="">All entities</option>
                {ENTITY_OPTIONS.map((option) => <option value={option} key={option}>{readable(option)}</option>)}
              </select>
            </label>
            <label>
              <span>Action</span>
              <select value={action} onChange={(event) => updateFilter(setAction, event.target.value)}>
                <option value="">All actions</option>
                {ACTION_OPTIONS.map((option) => <option value={option} key={option}>{readable(option)}</option>)}
              </select>
            </label>
            <label>
              <span>From</span>
              <div className="audit-date-field"><CalendarDays size={15} /><input type="date" value={from} max={to || undefined} onChange={(event) => updateFilter(setFrom, event.target.value)} /></div>
            </label>
            <label>
              <span>To</span>
              <div className="audit-date-field"><CalendarDays size={15} /><input type="date" value={to} min={from || undefined} onChange={(event) => updateFilter(setTo, event.target.value)} /></div>
            </label>
            {hasFilters && (
              <button className="audit-clear" type="button" onClick={clearFilters}>
                <Eraser size={15} /> Clear filters
              </button>
            )}
          </section>

          <section className="admin-panel audit-table-panel" aria-label="Recorded administrative actions">
            <div className="panel-heading audit-table-heading">
              <h2><ShieldCheck size={18} /> Recorded actions</h2>
              <span>{loading ? "Updating..." : `${visibleStart}-${visibleEnd} of ${total.toLocaleString()}`}</span>
            </div>
            {error ? (
              <div className="audit-error-state" role="alert">
                <span><AlertTriangle size={20} /></span>
                <div>
                  <strong>Audit records could not be loaded</strong>
                  <p>{error}</p>
                </div>
                <button type="button" onClick={() => setRequestVersion((version) => version + 1)}>
                  <RefreshCw size={15} /> Retry
                </button>
              </div>
            ) : (
              <>
                <div className="audit-scroll-hint"><MoveHorizontal size={15} /> Swipe to view all columns</div>
                <div className="audit-table-scroll" role="region" aria-label="Scrollable audit records" tabIndex="0">
                  <SmartDataTable
                    columns={columns}
                    data={logs}
                    loading={loading}
                    emptyMessage={hasFilters ? "No audit logs match these filters." : "No audit activity has been recorded."}
                    paginationPerPage={LIMIT}
                    showSearch={false}
                    pagination={false}
                    sortServer
                    onSort={handleSort}
                    defaultSortFieldId="createdAt"
                    defaultSortAsc={false}
                  />
                </div>
                <div className="audit-pagination" aria-label="Audit log pagination">
                  <span>Page <strong>{page}</strong> of <strong>{totalPages}</strong></span>
                  <div>
                    <button type="button" onClick={() => setPage((current) => Math.max(1, current - 1))} disabled={loading || page <= 1} aria-label="Previous page">
                      <ChevronLeft size={16} /> Previous
                    </button>
                    <button type="button" onClick={() => setPage((current) => Math.min(totalPages, current + 1))} disabled={loading || page >= totalPages} aria-label="Next page">
                      Next <ChevronRight size={16} />
                    </button>
                  </div>
                </div>
              </>
            )}
          </section>
        </div>
      </main>
    </div>
  );
}
